/**
 * ForYouSection
 *
 * A personalised horizontal tour scroll row shown on the homepage
 * when the user has selected interests via the InterestPickerScreen.
 *
 * - Reads interests from localStorage (via UserInterestsContext)
 * - Filters tours by selected categories
 * - Respects the existing admin sort order within the filtered set
 * - Hidden completely if user skipped the interest picker or selected nothing
 * - Does NOT alter any existing data or sort orders
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useUserInterests } from '../../contexts/UserInterestsContext';
import { tourService } from '../../services/tourService';
import { Tour } from '../../types';
import { optimizeImageUrl } from '../../utils/optimizeImageUrl';

const ForYouSection: React.FC = () => {
  const { interests, hasSeenPicker } = useUserInterests();
  const [tours, setTours] = useState<Tour[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Only show if picker was completed AND at least one interest selected
    if (!hasSeenPicker || interests.length === 0) return;

    const load = async () => {
      setLoading(true);
      try {
        const allTours = await tourService.getAllTours(false);
        const matched = allTours.filter((t: any) => {
          const cat = (t.category || '').toLowerCase();
          const catId = (t.categoryId || '').toLowerCase();
          return interests.some(
            (i) => i.toLowerCase() === cat || i.toLowerCase() === catId
          );
        });
        setTours(matched.slice(0, 12)); // Cap at 12 for the scroll row
      } catch {
        // Silent fail — section simply doesn't render
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [hasSeenPicker, interests]);

  // Don't render the section if no interests selected or no matching tours
  if (!hasSeenPicker || interests.length === 0 || (tours.length === 0 && !loading)) {
    return null;
  }

  return (
    <section className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Section header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-xl sm:text-2xl font-extrabold text-slate-900 dark:text-white">
            Picked for you
          </h2>
          <p className="text-sm text-slate-400 dark:text-slate-500 mt-0.5 font-medium">
            Based on your interests:{' '}
            <span className="text-primary-600 font-semibold">
              {interests.join(' · ')}
            </span>
          </p>
        </div>
        <Link
          to="/tours"
          className="text-sm font-semibold text-primary-600 hover:text-primary-800 transition-colors hidden sm:block"
        >
          See all →
        </Link>
      </div>

      {/* Horizontal scroll row */}
      {loading ? (
        <div 
          className="flex-nowrap-forced gap-4 overflow-x-auto pb-4 scrollbar-blue overscroll-contain"
        >
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="shrink-0 rounded-2xl bg-placeholder-blue dark:bg-slate-800 animate-pulse"
              style={{ width: '12rem', minWidth: '12rem', height: '12rem' }}
            />
          ))}
        </div>
      ) : (
        <div 
          className="flex-nowrap-forced gap-4 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-blue overscroll-contain"
        >
          {tours.map((tour) => {
            const image = tour.images?.[0];
            const price = tour.price;

            return (
              <Link
                key={tour.id}
                to={`/tours/${tour.id}`}
                className="shrink-0 snap-start group"
                style={{ width: '12rem', minWidth: '12rem' }}
              >

                <div className="relative h-32 sm:h-40 rounded-2xl overflow-hidden bg-placeholder-blue dark:bg-slate-800 mb-2 shadow-sm group-hover:shadow-md transition-shadow">

                  {image ? (
                    <img
                      src={optimizeImageUrl(image, { width: 400, quality: 80 })}
                      alt={tour.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-[#2A7ECE]/20 to-[#1E63A8]/30 flex items-center justify-center">
                      <span className="text-4xl">🌍</span>
                    </div>
                  )}
                  {/* Category badge */}
                  <div className="absolute top-2 left-2">
                    <span className="bg-white/90 backdrop-blur-sm text-primary-600 text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full shadow-sm">
                      {tour.category || 'Safari'}
                    </span>
                  </div>
                </div>
                <h3 className="text-xs sm:text-sm font-bold text-slate-900 dark:text-white line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors">
                  {tour.title}
                </h3>
                {price && (
                  <p className="text-[10px] sm:text-xs text-slate-400 dark:text-slate-500 mt-0.5 font-medium">
                    From ${Number(price).toLocaleString()}
                  </p>
                )}
              </Link>
            );
          })}
        </div>
      )}


      {/* Mobile see-all link */}
      <div className="mt-3 sm:hidden text-center">
        <Link
          to="/tours"
          className="text-sm font-semibold text-[#2A7ECE] hover:text-[#1E63A8] transition-colors"
        >
          See all tours →
        </Link>
      </div>
    </section>
  );
};

export default ForYouSection;
